import { useState } from 'react';
import type { FormEvent } from 'react';
import SEOHead from '@/components/base/SEOHead';

export default function LibroReclamaciones() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('contact_alt')?.toString().trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    // Remove honeypot from data
    const cleanData = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'contact_alt' && value) {
        cleanData.append(key, value.toString());
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/da2896tv7gurs51ef8u0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: cleanData.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else if (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam')) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || 'Hubo un error al registrar su reclamo. Intente de nuevo.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Error de conexión. Por favor, intente de nuevo más tarde.');
    }
  };

  return (
    <>
      <SEOHead
        title="Libro de Reclamaciones | Suvi Digital"
        description="Libro de Reclamaciones de Suvi Digital. Registra tu queja, reclamo o sugerencia sobre nuestros servicios de marketing digital."
        keywords="libro reclamaciones Suvi Digital, queja agencia marketing, reclamo servicio digital"
        canonical="https://suvidigital.com/libro-de-reclamaciones"
        schemaType="WebPage"
      />
      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[380px] flex items-center overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-primary-100 via-background-50 to-secondary-100" />
          <div className="absolute top-20 right-20 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl animate-float-slow" />

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 xl:px-16 pt-24 md:pt-28">
            <div className="max-w-4xl">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-5 border border-primary-200/50">
                Atención al Cliente
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground-900 mb-5 leading-tight">
                Libro de{' '}
                <span className="text-gradient">Reclamaciones</span>
              </h1>
              <p className="text-foreground-500 text-lg leading-relaxed max-w-2xl">
                Conforme a la Ley N° 29571 - Código de Protección y Defensa del Consumidor
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white py-14 md:py-18">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="max-w-3xl mx-auto">
              {/* Company Info */}
              <div className="bg-background-50 border border-background-200/60 rounded-2xl p-6 md:p-8 mb-6">
                <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-4">
                  Datos del Proveedor
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-foreground-500">Razón Social</p>
                    <p className="font-medium text-foreground-900">Suvi Digital E.I.R.L.</p>
                  </div>
                  <div>
                    <p className="text-foreground-500">RUC</p>
                    <p className="font-medium text-foreground-900">20608239172</p>
                  </div>
                  <div>
                    <p className="text-foreground-500">Dirección</p>
                    <p className="font-medium text-foreground-900">Lima, Perú</p>
                  </div>
                  <div>
                    <p className="text-foreground-500">Teléfono</p>
                    <p className="font-medium text-foreground-900">+51 989 307 864</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-foreground-500">Email</p>
                    <p className="font-medium text-foreground-900">hernan@suvidigital.com</p>
                  </div>
                </div>
              </div>

              {/* Reclaim Form */}
              <div className="bg-background-50 border border-background-200/60 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-2">
                  Registro de Reclamo
                </h2>
                <p className="text-foreground-500 text-sm mb-6">
                  Complete el siguiente formulario para registrar su queja o reclamo. Responderemos en un plazo máximo de 30 días calendario.
                </p>

                {status === 'success' && (
                  <div className="bg-accent-50 border border-accent-200 rounded-xl p-4 mb-5">
                    <p className="text-accent-700 text-sm font-medium flex items-center gap-2">
                      <i className="ri-check-line" />
                      Su reclamo ha sido registrado correctamente. Le responderemos a la brevedad.
                    </p>
                  </div>
                )}

                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
                    <p className="text-red-700 text-sm font-medium flex items-center gap-2">
                      <i className="ri-error-warning-line" />
                      {errorMsg}
                    </p>
                  </div>
                )}

                <form
                  data-readdy-form
                  id="reclamo-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="tipo" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Tipo *
                      </label>
                      <select
                        id="tipo"
                        name="tipo"
                        required
                        className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                      >
                        <option value="">Seleccione</option>
                        <option value="queja">Queja</option>
                        <option value="reclamo">Reclamo</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="fecha_incidente" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Fecha del incidente *
                      </label>
                      <input
                        type="date"
                        id="fecha_incidente"
                        name="fecha_incidente"
                        required
                        className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Nombres completos *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        required
                        className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                        placeholder="Nombres"
                      />
                    </div>
                    <div>
                      <label htmlFor="apellido" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Apellidos completos *
                      </label>
                      <input
                        type="text"
                        id="apellido"
                        name="apellido"
                        required
                        className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                        placeholder="Apellidos"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="doc_tipo" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Tipo de documento *
                      </label>
                      <select
                        id="doc_tipo"
                        name="doc_tipo"
                        required
                        className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                      >
                        <option value="">Seleccione</option>
                        <option value="dni">DNI</option>
                        <option value="ce">Carnet de Extranjería</option>
                        <option value="pasaporte">Pasaporte</option>
                        <option value="ruc">RUC</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="doc_numero" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Número de documento *
                      </label>
                      <input
                        type="text"
                        id="doc_numero"
                        name="doc_numero"
                        required
                        className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                        placeholder="Número"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                        placeholder="su@email.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        required
                        className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                        placeholder="+51 999 999 999"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="servicio" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Servicio contratado *
                    </label>
                    <select
                      id="servicio"
                      name="servicio"
                      required
                      className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                    >
                      <option value="">Seleccione</option>
                      <option value="marketing">Marketing Digital</option>
                      <option value="branding">Branding / Identidad Visual</option>
                      <option value="web">Diseño Web / E-commerce</option>
                      <option value="redes">Redes Sociales</option>
                      <option value="ads">Publicidad Digital</option>
                      <option value="seo">SEO / Analítica</option>
                      <option value="operturi">Software Operturi</option>
                      <option value="plataforma">Plataforma a Medida</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="detalle" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Detalle del reclamo / queja *
                    </label>
                    <textarea
                      id="detalle"
                      name="detalle"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all resize-none"
                      placeholder="Describa detalladamente su reclamo o queja..."
                    />
                    <p className="text-foreground-400 text-xs mt-1">Máximo 500 caracteres</p>
                  </div>

                  <div>
                    <label htmlFor="pedido" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Pedido del consumidor *
                    </label>
                    <textarea
                      id="pedido"
                      name="pedido"
                      required
                      rows={3}
                      maxLength={500}
                      className="w-full px-3.5 py-2.5 border border-background-300/60 rounded-xl text-sm text-foreground-900 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all resize-none"
                      placeholder="¿Qué solución espera?"
                    />
                    <p className="text-foreground-400 text-xs mt-1">Máximo 500 caracteres</p>
                  </div>

                  {/* Honeypot */}
                  <div className="hp-field">
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors whitespace-nowrap btn-glow"
                  >
                    Registrar Reclamo
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}